import withDraft from '@/utils/draftHelper';
import { states } from '@/constants/lifecycle';
import { responseError } from '@/utils/errors';
import {
  assignIfExists,
  assignValue,
  findResultInCategory,
  isValidFilter,
  mapItemOrderByValues,
  mapResults,
  removeItemByCode,
  selectableResult,
} from '@/utils/resultsHelper';
import { isCommonsModeEnabled } from '@/auth/featureToggles';
import { mapResult as mapDatasetResult } from '@/datasets/state/state';
import actions from '../constants';
import {
  findCollectionDataset,
  datasetAssigningCollection,
  newCollectionDataset,
  collectionDatasets,
} from './state';

const addCollectionDatasetError = withDraft((draft) => {
  draft.datasetAssigningCollection = datasetAssigningCollection();
});

const addCollectionDatasets = withDraft((draft) => {
  draft.datasetAssigningCollection.saveStatus = states.PENDING;
});

const addCollectionDatasetsAssignable = withDraft((draft, action) => {
  const item = findCollectionDataset(draft, action);
  const datasetsToAdd = draft.datasetAssigningCollection.datasetsToAdd.map((ds) => newCollectionDataset(ds));
  draft.datasetAssigningCollection.datasetsToAdd = [];
  item.datasetsToAdd.results = item.datasetsToAdd.results.concat(datasetsToAdd);
  item.unassignedDatasets.filter = '';
});

const addCollectionDatasetsSuccess = withDraft((draft) => {
  draft.datasetAssigningCollection = datasetAssigningCollection();
});

const fetchCollectionDatasets = withDraft((draft, action) => {
  const item = findCollectionDataset(draft, action);
  if (item) {
    item.readStatus = states.PENDING;
    item.readErrors = [];
    item.results = [];
  } else {
    draft.collectionDatasets[action.category].results.push({
      ...collectionDatasets(20, 0, false, true, true, isCommonsModeEnabled()),
      code: action.code,
      readStatus: states.PENDING,
      results: [],
      readErrors: [],
    });
  }
});

const fetchCollectionDatasetsAssignable = withDraft((draft, { category, code, filter }) => {
  const { unassignedDatasets } = findCollectionDataset(draft, { category, code });
  unassignedDatasets.filter = filter;
  unassignedDatasets.results = [];
  if (isValidFilter(filter, unassignedDatasets)) {
    unassignedDatasets.readStatus = states.PENDING;
    unassignedDatasets.readErrors = [];
  }
});

const fetchCollectionDatasetsAssignableError = withDraft((draft, action) => {
  const { unassignedDatasets } = findCollectionDataset(draft, action);
  unassignedDatasets.readStatus = states.ERROR;
  unassignedDatasets.readErrors = [responseError(action)];
});

const fetchCollectionDatasetsAssignableSuccess = withDraft((draft, action) => {
  const { unassignedDatasets } = findCollectionDataset(draft, action);
  const results = action.results.map(selectableResult);
  unassignedDatasets.readStatus = states.SUCCESS;
  unassignedDatasets.readErrors = [];
  unassignedDatasets.results = results;
  unassignedDatasets.original = results;
  unassignedDatasets.count = action.resultsCount;
});

const hideAddCollectionDatasets = withDraft((draft, action) => {
  draft.datasetAssigningCollection = datasetAssigningCollection();
  const item = findCollectionDataset(draft, action);
  item.datasetsToAdd = collectionDatasets(10000, 0, true, false, true, isCommonsModeEnabled());
  item.unassignedDatasets = collectionDatasets(20, 3, true, false, true, isCommonsModeEnabled());
});

const removeCollectionDatasetAssignable = withDraft((draft, { category, code, datasetCode }) => {
  const { datasetsToAdd } = findCollectionDataset(draft, { category, code });
  datasetsToAdd.results = removeItemByCode(datasetsToAdd.results, { code: datasetCode });
});

const removeCollectionDatasets = withDraft((draft, { category, code, items }) => {
  const item = findCollectionDataset(draft, { category, code });
  items.forEach((dataset) => {
    const removingDataset = findResultInCategory(item, dataset.code);
    if (removingDataset) {
      removingDataset.destroyStatus = states.PENDING;
      removingDataset.destroyErrors = [];
    }
  });
});

const removeCollectionDatasetsError = withDraft((draft, { category, code, items }) => {
  const item = findCollectionDataset(draft, { category, code });
  items.forEach((dataset) => {
    const removingDataset = findResultInCategory(item, dataset.code);
    if (removingDataset) {
      removingDataset.destroyStatus = states.ERROR;
      removingDataset.destroyErrors = [dataset.error];
    }
  });
});

const removeCollectionDatasetsSuccess = withDraft((draft, { category, code, items }) => {
  const item = findCollectionDataset(draft, { category, code });
  items.forEach((dataset) => {
    const removingDataset = findResultInCategory(item, dataset.code);
    if (removingDataset) {
      item.results = removeItemByCode(item.results, removingDataset);
    }
  });
});

const showAddCollectionDatasets = withDraft((draft, action) => {
  draft.datasetAssigningCollection.open = true;
  draft.datasetAssigningCollection.category = action.category;
  draft.datasetAssigningCollection.code = action.code;
  draft.datasetAssigningCollection.name = action.name;
  const item = findCollectionDataset(draft, action);
  item.datasetsToAdd = collectionDatasets(10000, 0, true, true, true, isCommonsModeEnabled());
  item.unassignedDatasets = collectionDatasets(20, 3, true, true, true, isCommonsModeEnabled());
});

const showCollectionDatasetsError = withDraft((draft, action) => {
  const item = findCollectionDataset(draft, action);
  item.readStatus = states.ERROR;
  item.readErrors = [responseError(action)];
});

const showCollectionDatasetsResults = withDraft((draft, action) => {
  const item = findCollectionDataset(draft, action);
  item.readStatus = states.SUCCESS;
  item.readErrors = [];
  item.name = action.name;
  item.permissions = action.permissions;
  item.results = action.append
    ? mapResults(item.results, action, mapDatasetResult)
    : mapResults([], action, mapDatasetResult);
  item.pageSize = action.pageSize;
  item.count = action.resultsCount;
  item.currentPage = action.currentPage;
  item.totalPages = action.totalPages;
});

const toggleCollectionDatasetColumnOrderBy = withDraft((draft, { category, code, orderByColumn }) => {
  const item = findCollectionDataset(draft, { category, code });
  item.readStatus = states.PENDING;
  item.readErrors = [];
  item.orderBy = mapItemOrderByValues(item.orderBy, orderByColumn, item.singleSorting);
});

const toggleCollectionDatasetSelected = withDraft((draft, { category, code, datasetCode }) => {
  const item = findCollectionDataset(draft, { category, code });
  const dataset = findResultInCategory(item, datasetCode);
  dataset.selected = !dataset.selected;
});

const toggleCollectionDatasetSingleSorting = withDraft((draft, action) => {
  const item = findCollectionDataset(draft, action);
  item.readStatus = states.PENDING;
  item.readErrors = [];
  item.singleSorting = !item.singleSorting;
  item.orderBy = [item.orderBy[0]];
});

const toggleCollectionDatasetsSelected = withDraft((draft, { category, code, selected }) => {
  const item = findCollectionDataset(draft, { category, code });
  item.results.forEach((result) => { assignValue(result, 'selected', selected); });
});

const updateCollectionDatasetsAssignableProperties = withDraft((draft, { properties }) => {
  assignIfExists(draft.datasetAssigningCollection, properties);
});

const updateCollectionDatasetsFilter = withDraft((draft, { category, code, value }) => {
  const item = findCollectionDataset(draft, { category, code });
  item.filter = value;
});

const updateCollectionDatasetsFlags = withDraft((draft, {
  category,
  code,
  privateFlag,
  internalFlag,
  publicFlag,
}) => {
  const item = findCollectionDataset(draft, { category, code });
  item.readStatus = states.PENDING;
  item.readErrors = [];
  item.privateFlag = privateFlag;
  item.internalFlag = internalFlag;
  item.publicFlag = publicFlag;
  item.currentPage = 1;
});

const updateCollectionUnassignedDatasetsProperties = withDraft((draft, { category, code, properties }) => {
  const item = findCollectionDataset(draft, { category, code });
  assignIfExists(item.unassignedDatasets, properties);
});

export default {
  [actions.ADD_COLLECTION_DATASET_ERROR]: addCollectionDatasetError,
  [actions.ADD_COLLECTION_DATASETS]: addCollectionDatasets,
  [actions.ADD_COLLECTION_DATASETS_ASSIGNABLE]: addCollectionDatasetsAssignable,
  [actions.ADD_COLLECTION_DATASETS_SUCCESS]: addCollectionDatasetsSuccess,
  [actions.FETCH_COLLECTION_DATASETS]: fetchCollectionDatasets,
  [actions.FETCH_COLLECTION_DATASETS_ASSIGNABLE]: fetchCollectionDatasetsAssignable,
  [actions.FETCH_COLLECTION_DATASETS_ASSIGNABLE_ERROR]: fetchCollectionDatasetsAssignableError,
  [actions.FETCH_COLLECTION_DATASETS_ASSIGNABLE_SUCCESS]: fetchCollectionDatasetsAssignableSuccess,
  [actions.HIDE_ADD_COLLECTION_DATASETS]: hideAddCollectionDatasets,
  [actions.REMOVE_COLLECTION_DATASET_ASSIGNABLE]: removeCollectionDatasetAssignable,
  [actions.REMOVE_COLLECTION_DATASETS]: removeCollectionDatasets,
  [actions.REMOVE_COLLECTION_DATASETS_ERROR]: removeCollectionDatasetsError,
  [actions.REMOVE_COLLECTION_DATASETS_SUCCESS]: removeCollectionDatasetsSuccess,
  [actions.SHOW_ADD_COLLECTION_DATASETS]: showAddCollectionDatasets,
  [actions.SHOW_COLLECTION_DATASETS_ERROR]: showCollectionDatasetsError,
  [actions.SHOW_COLLECTION_DATASETS_RESULTS]: showCollectionDatasetsResults,
  [actions.TOGGLE_COLLECTION_DATASET_COLUMN_ORDER_BY]: toggleCollectionDatasetColumnOrderBy,
  [actions.TOGGLE_COLLECTION_DATASET_SELECTED]: toggleCollectionDatasetSelected,
  [actions.TOGGLE_COLLECTION_DATASET_SINGLE_SORTING]: toggleCollectionDatasetSingleSorting,
  [actions.TOGGLE_COLLECTION_DATASETS_SELECTED]: toggleCollectionDatasetsSelected,
  [actions.UPDATE_COLLECTION_DATASETS_ASSIGNABLE_PROPERTIES]: updateCollectionDatasetsAssignableProperties,
  [actions.UPDATE_COLLECTION_DATASETS_FILTER]: updateCollectionDatasetsFilter,
  [actions.UPDATE_COLLECTION_DATASETS_FLAGS]: updateCollectionDatasetsFlags,
  [actions.UPDATE_COLLECTION_UNASSIGNED_DATASETS_PROPERTIES]: updateCollectionUnassignedDatasetsProperties,
};
